import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { Color3 } from '@babylonjs/core/Maths/math';

import { Observable } from '../../Utils/observable';
import { checkElementVisible } from '../../Utils/html';
import { ToolLoader } from '../Tool/toolLoader';
import { ToolMaterial } from '../Tool/toolMaterial';

/**
 * Manage all the essential assets needed to build a 3D scene (Engine, Scene Cameras, etc)
 */

export enum SystemEvent {
    Start, // System
    Stop, // System
    Begin, // SystemAnimation
    End, // SystemAnimation
    Resize, // SystemResponsive
    Scaling, // SystemResponsive
}

export class System extends Observable<SystemEvent, number> {
    /**
     * Allow to load any content only for this 3D Scene
     */
    public loader: ToolLoader;

    public toolMaterial: ToolMaterial;

    /**
    * Max Hardware scaling of BabylonJS Engine
    */
    protected maxScaling = 1;

    /**
    * BabylonJS Engine
    */
    public engine: Engine;

    /**
     * BabylonJS Scene
     */
    public scene: Scene;

    /**
     * Canvas used to draw the 3D scene
     */
    public canvas: HTMLCanvasElement;

    /**
     * Creates a new System
     * @param canvas Element where the scene will be drawn
     */
    constructor(canvas: HTMLCanvasElement) {
        super('System');
        // if (!Engine.isSupported()) throw 'WebGL not supported';
        this.canvas = canvas;
        // For now keep false as the last argument of the engine,
        // We don't want the canvas to adapt to screen ratio as it slow down too much the scene
        // preserveDrawingBuffer and stencil needed for screenshot
        // premultipliedAlpha needed for .HDR to .ENV transformation
        const engineOption = {
            limitDeviceRatio: this.maxScaling,
            preserveDrawingBuffer: true,
            stencil: true,
            premultipliedAlpha: false
        };

        this.engine = new Engine(this.canvas, true, engineOption, false);
        // NOTE to avoid request for manifest files because it can block loading on safari
        this.engine.enableOfflineSupport = false;
        this.buildScene();

        this.loader = new ToolLoader(this.scene);
        this.toolMaterial = new ToolMaterial();
    }

    /**
    * @ignore
    */
    private buildScene() {
        this.scene = new Scene(this.engine);
        this.scene.shadowsEnabled = false;
        this.scene.ambientColor = new Color3(1, 1, 1);

        // Avoid automatic pick and improve performance
        // Can't use it now as it breaks GUI events
        // this.scene.detachControl();
    }

    /**
    * Check if element visible by the screen
    */

    protected containerVisible = false;

    protected checkVisible(): boolean {
        const containerVisible = checkElementVisible(this.canvas);
        if (!containerVisible) this.engine.setSize(0, 0);
        else if (containerVisible && !this.containerVisible) {
            if (!this.isCheckingReady) {
                this.engine.resize();
                this.scene.render();
            }
        }
        this.containerVisible = containerVisible;
        return this.containerVisible;
    }

    protected isCheckingReady = true;

    private showTimeReady = false;

    public checkSceneReadyToRender(callback: () => void): void {
        this.isCheckingReady = true;
        const time = performance.now();
        // Make sure scene is ready
        this.scene.executeWhenReady(() => {
            const ntime = performance.now();
            if (this.showTimeReady) console.log('READY', Math.round(ntime - time));
            this.isCheckingReady = false;
            // Force to have correct scene parameters
            this.notify(SystemEvent.Stop, 0);
            this.scene.render();
            //! To make sure everything needed is shown
            this.executeOnceBeforeRender(() => {
                this.checkActiveMeshes();
            });
            callback();
        });
    }

    public executeOnceBeforeRender(func: () => void): void {
        const execFunc = () => {
            this.scene.unregisterAfterRender(execFunc);
            func();
        };
        this.scene.registerBeforeRender(execFunc);
    }

    /**
     * Optimize scene to make rendering faster
     * https://doc.babylonjs.com/how_to/optimizing_your_scene#reducing-shaders-overhead
     */
    public optimize(): void {
        this.scene.autoClear = false; // Color buffer

        //* Can't do autoClearDepthAndStencil as it bugs
        // this.scene.autoClearDepthAndStencil = false; // Depth and stencil, obviously

        // this.scene.clearCachedVertexData();
        // this.scene.cleanCachedTextureBuffer();
        // let activeTest = 0;
        // this.scene.registerBeforeRender(() => {
        //     activeTest++;
        //     this.scene.freezeActiveMeshes();
        //     if (activeTest > 30) {
        //         activeTest = 0;
        //         this.scene.unfreezeActiveMeshes();
        //     }
        // });
    }

    /**
     * UnOptimize scene rendering
     * https://doc.babylonjs.com/how_to/optimizing_your_scene#reducing-shaders-overhead
     */
    public unOptimize(): void {
        this.scene.autoClear = true; // Color buffer
        // this.scene.autoClearDepthAndStencil = true; // Depth and stencil, obviously
    }

    /**
     * Optimize scene to make rendering faster
     * https://doc.babylonjs.com/how_to/optimizing_your_scene#reducing-shaders-overhead
     */
    public optimizeHard(): void {
        this.optimize();
        this.scene.freezeActiveMeshes();
        // Can not use blockMaterial or imageProcessing does't work in pipeline
        // this.scene.blockMaterialDirtyMechanism = true;
    }

    /**
     * UnOptimize scene rendering
     * https://doc.babylonjs.com/how_to/optimizing_your_scene#reducing-shaders-overhead
     */
    public unOptimizeHard(): void {
        this.unOptimize();
        this.scene.unfreezeActiveMeshes();
        // Can not use blockMaterial or imageProcessing does't work in pipeline
        // this.scene.blockMaterialDirtyMechanism = false;
    }

    public checkActiveMeshes(): void {
        this.scene.unfreezeActiveMeshes();
        this.scene.freezeActiveMeshes();
    }

    public disposeElements(): void {
        this.scene.meshes.forEach((mesh) => {
            //! Do not dispose Meshes as they can be used again
            //! when loading twice a model for instance
            // mesh.dispose()
            mesh.isVisible = false;
        });
        this.scene.meshes = [];
        this.scene.lights.forEach((light) => {
            light.dispose();
        });
        this.scene.lights = [];
    }

    public disposeScene(): void {
        this.scene.dispose();
        this.engine.dispose();
        this.canvas.parentElement.removeChild(this.canvas);
    }
}
